import {
  invoiceFormatForPrinterType,
  printerReadinessError,
  type JsonObject,
  type PrinterJobResponse,
  type StorePrinter,
} from "@medicorepay/shared";
import { printHtmlArtifact } from "../lib/revival-invoice-print";
import { productRevivalApi } from "./product-revival-client";

interface InvoiceArtifactResponse extends JsonObject {
  artifactId: string;
  jobId: string;
}

function assertPrinterReady(printer: StorePrinter) {
  const problem = printerReadinessError(printer);
  if (problem) throw new Error(problem);
}

async function transition(
  jobId: string,
  status: "DIALOG_OPENED" | "FAILED",
  failure?: { code: string; message: string },
) {
  return productRevivalApi.transitionPrinterJob(
    jobId,
    {
      status,
      ...(failure
        ? { failureCode: failure.code, failureMessage: failure.message }
        : {}),
    },
    {
      idempotencyKey: crypto.randomUUID(),
      clientApplication: "web",
    },
  );
}

export const printerDispatchService = {
  async createAndDispatchInvoice(input: {
    saleId: string;
    printer: StorePrinter;
    reason: string;
    printWindow?: Window | null;
  }): Promise<PrinterJobResponse> {
    let artifact: InvoiceArtifactResponse | null = null;
    try {
      assertPrinterReady(input.printer);
      if (input.printer.connectionType !== "SYSTEM") {
        throw new Error(
          "Direct network, serial and USB printers require MediCorePay Desktop; select a system printer for web printing",
        );
      }
      artifact = (await productRevivalApi.createInvoiceArtifact(
        input.saleId,
        {
          format: invoiceFormatForPrinterType(input.printer.printerType),
          printerId: input.printer.id,
          channel: "PRINT",
          reason: input.reason,
        },
        {
          idempotencyKey: crypto.randomUUID(),
          clientApplication: "web",
        },
      )) as InvoiceArtifactResponse;
      if (!artifact.artifactId || !artifact.jobId) {
        throw new Error("The server did not return a printable artifact job");
      }
      const blob = await productRevivalApi.downloadInvoiceArtifact(
        artifact.artifactId,
      );
      await printHtmlArtifact(await blob.text(), input.printWindow);
      return await transition(artifact.jobId, "DIALOG_OPENED");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Printer dispatch failed";
      if (artifact?.jobId) {
        try {
          await transition(artifact.jobId, "FAILED", {
            code: "PRINTER_DISPATCH_FAILED",
            message,
          });
        } catch {
          // Keep the original dispatch error visible to the operator.
        }
      }
      if (input.printWindow && !input.printWindow.closed)
        input.printWindow.close();
      throw error;
    }
  },
};
